import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaArrowLeft, FaCheck, FaShieldAlt, FaUserMd, FaLock } from 'react-icons/fa';
import { assessmentAPI } from '../../services/assessmentAPI';

const GLP1Eligibility = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    age: '',
    gender: '',
    heightFeet: '',
    heightInches: '',
    weight: '',
    // Step 2: Health Conditions
    hasType2Diabetes: null,
    hasHighBP: null,
    hasHighCholesterol: null,
    hasPCOS: null,
    hasSleepApnea: null,
    // Step 3: Weight History
    previousAttempts: [],
    yearsOverweight: '',
    weightLossGoal: '',
    // Step 4: Medical History
    hasThyroidDisorder: null,
    hasPancreatitis: null,
    hasGallbladder: null,
    isPregnant: null,
    hasEatingDisorder: null,
    currentMedications: '',
    // Step 5: Contact
    name: '',
    email: '',
    phone: '',
    city: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = 5;

  const calculateBMI = () => {
    const heightInInches = (parseInt(formData.heightFeet) * 12) + parseInt(formData.heightInches || 0);
    const heightInMeters = heightInInches * 0.0254;
    const weightInKg = parseFloat(formData.weight);
    if (heightInMeters > 0 && weightInKg > 0) {
      return (weightInKg / (heightInMeters * heightInMeters)).toFixed(1);
    }
    return null;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const currentValues = prev[field] || [];
      if (currentValues.includes(value)) {
        return { ...prev, [field]: currentValues.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...currentValues, value] };
      }
    });
  };

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.age || formData.age < 18 || formData.age > 75) {
        newErrors.age = 'Please enter a valid age (18-75)';
      }
      if (!formData.gender) newErrors.gender = 'Please select your gender';
      if (!formData.heightFeet) newErrors.heightFeet = 'Please enter height';
      if (!formData.weight) newErrors.weight = 'Please enter weight';
    }

    if (currentStep === 2) {
      // At least one question should be answered
      const healthFields = ['hasType2Diabetes', 'hasHighBP', 'hasHighCholesterol', 'hasPCOS', 'hasSleepApnea'];
      const answeredCount = healthFields.filter(f => formData[f] !== null).length;
      if (answeredCount < healthFields.length) {
        newErrors.health = 'Please answer all health questions';
      }
    }

    if (currentStep === 3) {
      if (formData.previousAttempts.length === 0) {
        newErrors.previousAttempts = 'Please select at least one option';
      }
      if (!formData.yearsOverweight) newErrors.yearsOverweight = 'Please select duration';
      if (!formData.weightLossGoal) newErrors.weightLossGoal = 'Please enter your goal';
    }

    if (currentStep === 4) {
      const medicalFields = ['hasThyroidDisorder', 'hasPancreatitis', 'hasGallbladder', 'isPregnant', 'hasEatingDisorder'];
      const answeredCount = medicalFields.filter(f => formData[f] !== null).length;
      if (answeredCount < medicalFields.length) {
        newErrors.medical = 'Please answer all medical questions';
      }
    }

    if (currentStep === 5) {
      if (!formData.name) newErrors.name = 'Please enter your name';
      if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData.phone || formData.phone.length < 10) {
        newErrors.phone = 'Please enter a valid phone number';
      }
      if (!formData.city) newErrors.city = 'Please enter your city';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo(0, 0);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const bmi = parseFloat(calculateBMI());

    // Convert height from feet/inches → cm for the edge function
    const heightInCm = Math.round(
      ((parseInt(formData.heightFeet) * 12) + parseInt(formData.heightInches || 0)) * 2.54
    );

    // Build healthConditions array from boolean fields
    const healthConditions = [];
    if (formData.hasType2Diabetes) healthConditions.push('type2_diabetes');
    if (formData.hasHighBP)         healthConditions.push('hypertension');
    if (formData.hasHighCholesterol) healthConditions.push('high_cholesterol');
    if (formData.hasPCOS)           healthConditions.push('pcos');
    if (formData.hasSleepApnea)     healthConditions.push('sleep_apnea');

    // Payload matched to edge function interface
    const payload = {
      contactName:               formData.name,
      contactEmail:              formData.email,
      contactPhone:              formData.phone,
      age:                       parseInt(formData.age),
      weight:                    parseFloat(formData.weight),
      height:                    heightInCm,
      gender:                    formData.gender,
      stateLocation:             formData.city,
      isPregnantOrBreastfeeding: formData.isPregnant ?? false,
      hasThyroidCancerHistory:   formData.hasThyroidDisorder ?? false,
      hasPancreatitisHistory:    formData.hasPancreatitis ?? false,
      hasType1Diabetes:          false,
      hasEatingDisorder:         formData.hasEatingDisorder ?? false,
      healthConditions,
      isTakingMedications:       !!formData.currentMedications,
      medicationsList:           formData.currentMedications || null,
      hasTriedWeightLoss:        formData.previousAttempts.length > 0,
      weightLossMethods:         formData.previousAttempts.join(', ') || null,
      primaryGoal:               formData.weightLossGoal ? `Lose ${formData.weightLossGoal} kg` : null,
    };

    // Save to Supabase via edge function — graceful degradation if it fails
    try {
      await assessmentAPI.submitAssessment(payload);
    } catch (err) {
      console.error('Assessment save failed:', err);
      // Still show result to user — don't block on DB failure
    } finally {
      setSubmitting(false);
    }

    // Local eligibility check drives the result page UI
    const eligibilityData = { ...formData, bmi, submittedAt: new Date().toISOString() };
    const isEligible = determineEligibility(eligibilityData);

    navigate('/result', { state: { eligibilityData, isEligible, bmi } });
  };

  const determineEligibility = (data) => {
    const bmi = data.bmi;

    // Exclusion criteria
    if (data.isPregnant) return { eligible: false, reason: 'pregnancy' };
    if (data.hasEatingDisorder) return { eligible: false, reason: 'eating_disorder' };
    if (data.hasPancreatitis) return { eligible: false, reason: 'pancreatitis' };
    if (parseInt(data.age) < 18) return { eligible: false, reason: 'age_under_18' };

    // BMI criteria
    if (bmi >= 30) {
      return { eligible: true, category: 'obese', reason: 'bmi_30_plus' };
    }

    if (bmi >= 27) {
      // Check for weight-related conditions
      const hasCondition = data.hasType2Diabetes || data.hasHighBP ||
                          data.hasHighCholesterol || data.hasSleepApnea || data.hasPCOS;
      if (hasCondition) {
        return { eligible: true, category: 'overweight_with_condition', reason: 'bmi_27_with_comorbidity' };
      }
      return { eligible: false, reason: 'bmi_27_no_condition' };
    }

    return { eligible: false, reason: 'bmi_under_27' };
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between mb-2">
        {[1, 2, 3, 4, 5].map((step) => (
          <div
            key={step}
            className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold text-sm
              ${currentStep >= step
                ? 'bg-gold-500 text-white'
                : 'bg-gray-200 text-gray-500'}`}
          >
            {currentStep > step ? <FaCheck /> : step}
          </div>
        ))}
      </div>
      <div className="relative h-2 bg-gray-200 rounded-full">
        <div
          className="absolute h-2 bg-gold-500 rounded-full transition-all duration-300"
          style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
        />
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        <span>Basic Info</span>
        <span>Health</span>
        <span>History</span>
        <span>Medical</span>
        <span>Contact</span>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-navy-900 mb-2">Let's Start With The Basics</h2>
      <p className="text-gray-600 mb-6">This helps us calculate your BMI and assess eligibility.</p>

      {/* Age */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Age *</label>
        <input
          type="number"
          value={formData.age}
          onChange={(e) => handleInputChange('age', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500
            ${errors.age ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Enter your age"
        />
        {errors.age && <p className="mt-1 text-sm text-red-500">{errors.age}</p>}
      </div>

      {/* Gender */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
        <div className="grid grid-cols-3 gap-4">
          {['male', 'female', 'other'].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleInputChange('gender', option)}
              className={`py-3 px-4 border rounded-lg font-medium capitalize transition-all
                ${formData.gender === option
                  ? 'bg-navy-900 text-white border-navy-900'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-navy-900'}`}
            >
              {option}
            </button>
          ))}
        </div>
        {errors.gender && <p className="mt-1 text-sm text-red-500">{errors.gender}</p>}
      </div>

      {/* Height */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Height *</label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <input
              type="number"
              value={formData.heightFeet}
              onChange={(e) => handleInputChange('heightFeet', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold-500
                ${errors.heightFeet ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Feet"
            />
          </div>
          <div>
            <input
              type="number"
              value={formData.heightInches}
              onChange={(e) => handleInputChange('heightInches', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500"
              placeholder="Inches"
            />
          </div>
        </div>
        {errors.heightFeet && <p className="mt-1 text-sm text-red-500">{errors.heightFeet}</p>}
      </div>

      {/* Weight */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Current Weight (kg) *</label>
        <input
          type="number"
          value={formData.weight}
          onChange={(e) => handleInputChange('weight', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold-500
            ${errors.weight ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Enter weight in kg"
        />
        {errors.weight && <p className="mt-1 text-sm text-red-500">{errors.weight}</p>}
      </div>

      {/* BMI Preview */}
      {formData.heightFeet && formData.weight && (
        <div className="p-4 bg-navy-50 rounded-lg border border-navy-200">
          <p className="text-sm text-gray-600">Your calculated BMI:</p>
          <p className="text-2xl font-bold text-navy-900">{calculateBMI()}</p>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-navy-900 mb-2">Health Conditions</h2>
      <p className="text-gray-600 mb-6">Do you have any of the following conditions? (Select Yes or No for each)</p>

      {errors.health && (
        <p className="text-sm text-red-500 mb-4">{errors.health}</p>
      )}

      {[
        { field: 'hasType2Diabetes', label: 'Type 2 Diabetes or Pre-diabetes' },
        { field: 'hasHighBP', label: 'High Blood Pressure (Hypertension)' },
        { field: 'hasHighCholesterol', label: 'High Cholesterol' },
        { field: 'hasPCOS', label: 'PCOS (Polycystic Ovary Syndrome)' },
        { field: 'hasSleepApnea', label: 'Sleep Apnea' },
      ].map(({ field, label }) => (
        <div key={field} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <span className="text-gray-700 font-medium">{label}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleInputChange(field, true)}
              className={`px-6 py-2 rounded-lg font-medium transition-all
                ${formData[field] === true
                  ? 'bg-navy-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:border-navy-900'}`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => handleInputChange(field, false)}
              className={`px-6 py-2 rounded-lg font-medium transition-all
                ${formData[field] === false
                  ? 'bg-navy-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:border-navy-900'}`}
            >
              No
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-navy-900 mb-2">Your Weight Loss Journey</h2>
      <p className="text-gray-600 mb-6">Tell us about your previous weight loss attempts.</p>

      {/* Previous Attempts */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          What have you tried before? (Select all that apply) *
        </label>
        <div className="grid grid-cols-2 gap-3">
          {[
            'Dieting',
            'Exercise Programs',
            'Gym Membership',
            'Nutritionist/Dietitian',
            'Weight Loss Apps',
            'Meal Replacement',
            'Intermittent Fasting',
            'Other Medications',
          ].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleCheckboxChange('previousAttempts', option)}
              className={`p-3 border rounded-lg text-left font-medium transition-all
                ${formData.previousAttempts.includes(option)
                  ? 'bg-navy-900 text-white border-navy-900'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-navy-900'}`}
            >
              {option}
            </button>
          ))}
        </div>
        {errors.previousAttempts && (
          <p className="mt-2 text-sm text-red-500">{errors.previousAttempts}</p>
        )}
      </div>

      {/* Years Overweight */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          How long have you been struggling with weight? *
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['Less than 1 year', '1-3 years', '3-5 years', '5+ years'].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleInputChange('yearsOverweight', option)}
              className={`p-3 border rounded-lg font-medium transition-all
                ${formData.yearsOverweight === option
                  ? 'bg-navy-900 text-white border-navy-900'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-navy-900'}`}
            >
              {option}
            </button>
          ))}
        </div>
        {errors.yearsOverweight && (
          <p className="mt-2 text-sm text-red-500">{errors.yearsOverweight}</p>
        )}
      </div>

      {/* Weight Loss Goal */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How much weight would you like to lose? (kg) *
        </label>
        <input
          type="number"
          value={formData.weightLossGoal}
          onChange={(e) => handleInputChange('weightLossGoal', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold-500
            ${errors.weightLossGoal ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Enter target weight loss in kg"
        />
        {errors.weightLossGoal && (
          <p className="mt-1 text-sm text-red-500">{errors.weightLossGoal}</p>
        )}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-navy-900 mb-2">Medical History</h2>
      <p className="text-gray-600 mb-6">
        These questions help ensure GLP-1 medication is safe for you.
      </p>

      {errors.medical && (
        <p className="text-sm text-red-500 mb-4">{errors.medical}</p>
      )}

      {[
        { field: 'hasThyroidDisorder', label: 'History of thyroid cancer or MEN 2 syndrome' },
        { field: 'hasPancreatitis', label: 'History of pancreatitis' },
        { field: 'hasGallbladder', label: 'Gallbladder problems' },
        { field: 'isPregnant', label: 'Currently pregnant or planning pregnancy' },
        { field: 'hasEatingDisorder', label: 'History of eating disorders' },
      ].map(({ field, label }) => (
        <div key={field} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <span className="text-gray-700 font-medium">{label}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleInputChange(field, true)}
              className={`px-6 py-2 rounded-lg font-medium transition-all
                ${formData[field] === true
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:border-navy-900'}`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => handleInputChange(field, false)}
              className={`px-6 py-2 rounded-lg font-medium transition-all
                ${formData[field] === false
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:border-navy-900'}`}
            >
              No
            </button>
          </div>
        </div>
      ))}

      {/* Current Medications */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Current Medications (Optional)
        </label>
        <textarea
          value={formData.currentMedications}
          onChange={(e) => handleInputChange('currentMedications', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500"
          placeholder="List any medications you're currently taking..."
        />
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-navy-900 mb-2">Almost Done!</h2>
      <p className="text-gray-600 mb-6">
        Enter your contact details to receive your personalized recommendation.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold-500
            ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Enter your full name"
        />
        {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold-500
            ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Enter your email"
        />
        {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold-500
            ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Enter your phone number"
        />
        {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
        <input
          type="text"
          value={formData.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold-500
            ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Enter your city"
        />
        {errors.city && <p className="mt-1 text-sm text-red-500">{errors.city}</p>}
      </div>

      {/* Trust Indicators */}
      {/* <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <FaLock className="text-green-600" />
            <span>256-bit encryption</span>
          </div>
          <div className="flex items-center gap-2">
            <FaShieldAlt className="text-green-600" />
            <span>HIPAA compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <FaUserMd className="text-green-600" />
            <span>Doctor reviewed</span>
          </div>
        </div>
      </div> */}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return renderStep1();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-2">
            Check Your Eligibility
          </h1>
          <p className="text-gray-600">
            Takes less than 3 minutes. 100% confidential.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          {renderProgressBar()}

          <form onSubmit={(e) => e.preventDefault()}>
            {renderCurrentStep()}

            {/* Navigation Buttons */}
            <div className="mt-8 flex justify-between gap-4">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FaArrowLeft />
                  Back
                </button>
              )}

              <button
                type="button"
                onClick={handleNext}
                disabled={submitting}
                className={`flex items-center gap-2 px-8 py-3 bg-gold-500 hover:bg-gold-600 text-white rounded-lg font-semibold transition-colors shadow-lg disabled:opacity-70 disabled:cursor-not-allowed
                  ${currentStep === 1 ? 'w-full justify-center' : 'ml-auto'}`}
              >
                {submitting ? 'Processing...' : currentStep === totalSteps ? 'Get My Results' : 'Continue'}
                {!submitting && <FaArrowRight />}
              </button>
            </div>
          </form>
        </div>

        {/* Trust Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Your information is secure and will only be reviewed by licensed physicians.</p>
        </div>
      </div>
    </div>
  );
};

export default GLP1Eligibility;
